<script lang="ts">
	import type { Organization } from '../api';
	import Icon from './Icon.svelte';

	let {
		isOpen,
		organization,
		isOwner,
		onClose,
		onEdit,
		onDelete
	}: {
		isOpen: boolean;
		organization: Organization | null;
		isOwner: boolean;
		onClose: () => void;
		onEdit: (organization: Organization) => void;
		onDelete: (organization: Organization) => void;
	} = $props();

	function handleEdit() {
		if (organization) {
			onEdit(organization);
		}
	}

	function handleDelete() {
		if (organization) {
			onClose();
			onDelete(organization);
		}
	}
</script>

{#if isOpen && organization}
	<div class="fixed inset-0 z-50 overflow-hidden">
		<div class="absolute inset-0 overflow-hidden">
			<div class="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10">
				<div class="pointer-events-auto relative w-screen max-w-md">
					<div class="flex h-full flex-col overflow-y-scroll bg-white py-6 shadow-xl">
						<div class="px-4 sm:px-6">
							<div class="flex items-start justify-between">
								<h2 class="text-xl font-semibold text-gray-900">Organization Details</h2>
								<div class="ml-3 flex h-7 items-center">
									<button
										type="button"
										onclick={onClose}
										class="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
									>
										<span class="sr-only">Close panel</span>
										<Icon name="x" size={24} />
									</button>
								</div>
							</div>
						</div>
						<div class="relative mt-6 flex-1 px-4 sm:px-6">
							<div class="space-y-6">
								<!-- Organization Name -->
								<div>
									<h3
										class="rounded-md border-0 bg-gray-50 px-3 py-1.5 text-lg font-medium text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300"
									>
										{organization.name}
									</h3>
									<p class="mt-1 text-sm text-gray-500">
										{organization.countryName || organization.country?.isoName || 'Unknown Country'}
									</p>
								</div>

								<!-- Your Role -->
								<div>
									<h4 class="text-sm font-medium text-gray-900">Your Role</h4>
									<span
										class="mt-1 inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium {isOwner
											? 'bg-blue-100 text-blue-800'
											: 'bg-gray-100 text-gray-800'}"
									>
										{isOwner ? 'Owner' : 'Member'}
									</span>
								</div>

								<!-- Statistics -->
								{#if organization.memberCount !== undefined}
									<div>
										<h4 class="text-sm font-medium text-gray-900">Members</h4>
										<p class="mt-1 flex items-center gap-1 text-sm text-gray-600">
											<Icon name="users" size={16} />
											{organization.memberCount} member{organization.memberCount === 1 ? '' : 's'}
										</p>
									</div>
								{/if}

								{#if organization.createdAt}
									<div>
										<h4 class="text-sm font-medium text-gray-900">Created</h4>
										<p class="mt-1 text-sm text-gray-500">
											{new Date(organization.createdAt).toLocaleDateString()}
										</p>
									</div>
								{/if}

								<!-- Actions -->
								{#if isOwner}
									<div class="border-t border-gray-200 pt-4">
										<h4 class="mb-3 text-sm font-medium text-gray-900">Actions</h4>
										<div class="space-y-2">
											<button
												onclick={handleEdit}
												class="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
											>
												<Icon name="pencil" size={16} />
												Edit Organization
											</button>
											<button
												onclick={handleDelete}
												class="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
											>
												<Icon name="trash" size={16} />
												Delete Organization
											</button>
										</div>
									</div>
								{/if}
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	</div>
{/if}
